<script lang="ts">
  /**
   * 類題 1 問（problem-format.md §2.5、§7）。最初は折りたたみで、題名・出典だけが見える。
   * 開くと 要約・「AtCoder で問題文を読む」・入出力例・エディタ・実行と採点・ヒント 3 段・模範解答。
   * 一度開いたら閉じても中身（採点の結果）は捨てない。書きかけのキーは `<problemId>/<exerciseId>`。
   */
  import { exerciseTimeLimit, problemDraftKey, sampleCases, sourceLabel } from '../../lib/problems';
  import SampleCases from './SampleCases.svelte';
  import SourceLink from './SourceLink.svelte';
  import Workbench from './Workbench.svelte';
  import type { DraftStore, ExerciseNode, ProblemMeta } from './types';

  interface Props {
    problem: ProblemMeta;
    exercise: ExerciseNode;
    drafts: DraftStore;
  }

  let { problem, exercise, drafts }: Props = $props();

  const uid = $props.id();
  let open = $state(false);
  let mounted = $state(false);

  const meta = $derived(exercise.meta);
  const limit = $derived(exerciseTimeLimit(problem, meta));

  function toggle() {
    open = !open;
    if (open) mounted = true;
  }
</script>

<li class="exercise" data-testid="exercise" data-exercise={exercise.id}>
  <h3 class="head">
    <button
      class="toggle"
      type="button"
      aria-expanded={open}
      aria-controls="{uid}-body"
      data-testid="exercise-toggle"
      data-exercise={exercise.id}
      onclick={toggle}
    >
      <span class="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      <span class="eyebrow">類題 {exercise.index}</span>
      <span class="title">{meta.title}</span>
      <span class="src">{sourceLabel(meta.source)}</span>
    </button>
  </h3>
  <div class="body" id="{uid}-body" hidden={!open}>
    {#if mounted}
      <p class="meta-line">
        <span>制限時間 <span class="num">{limit}</span> 秒</span>
        <SourceLink url={meta.source.url} testid="exercise-atcoder-link" />
      </p>
      <p class="summary">{meta.summary.replace(/\n$/, '')}</p>
      <h4 class="sub-title">入出力例</h4>
      <SampleCases cases={sampleCases(meta.cases)} />
      <Workbench
        kind="exercise"
        target={{ problemId: problem.id, exerciseId: exercise.id }}
        draftKey={problemDraftKey(problem.id, exercise.id)}
        starter={meta.starter}
        cases={meta.cases}
        solution={meta.solution}
        hints={exercise.hints}
        timeLimitSec={limit}
        {drafts}
        editorLabel="類題 {exercise.index} の Swift のコード"
        headingLevel={4}
      />
    {/if}
  </div>
</li>

<style>
  .exercise {
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-lg);
    background: var(--surface);
    min-width: 0;
  }
  .head {
    font-size: var(--fs-md);
  }
  .toggle {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: var(--space-1) var(--space-2);
    width: 100%;
    min-height: var(--tap-min);
    padding: var(--space-3) var(--space-4);
    border: 0;
    background: transparent;
    color: var(--text);
    font: inherit;
    font-weight: var(--fw-semibold);
    text-align: left;
    cursor: pointer;
    border-radius: var(--radius-lg);
  }
  .toggle:hover {
    background: var(--canvas);
  }
  .chev {
    width: 1em;
    color: var(--text-soft);
  }
  .src {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-regular);
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: 0 var(--space-4) var(--space-4);
    min-width: 0;
  }
  .body[hidden] {
    display: none;
  }
  .meta-line {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-4);
    margin: 0;
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .summary {
    margin: 0;
    white-space: pre-line;
  }
  .sub-title {
    font-size: var(--fs-md);
  }
</style>
